import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NimmstaCommonsModuleConfiguration } from '../nimmsta-commons-module.config';
import { Observable, catchError, map, of, zip } from 'rxjs';

type SbomConfiguration = {
  metadata?: {
    component?: {
      'bom-ref': string;
    };
  };
  components?: [
    {
      group: string;
      name: string;
      version: string;
      licenses: SbomLicense[];
      'bom-ref': string;
    },
  ];
  dependencies?: SbomDependency[];
};

type SbomDependency = {
  ref: string;
  dependsOn: string[];
};

type SbomLicense = {
  license?: {
    id?: string;
    name?: string;
    url?: string;
  };
  expression?: string;
};

export type License = {
  name: string;
  dependencies: Dependency[];
};

export type Dependency = {
  group: string;
  name: string;
  version: string;
  licenseUrl?: string;
};

const LICENSE_PRIORITY = [
  'MIT',
  'Apache-2.0',
  'MPL-2.0',
  'EPL-2.0',
  'EPL-1.0',
  'BSD-2-Clause',
  'BSD-3-Clause',
  'LGPL-2.1',
  'LGPL-3.0',
];

const OVERRIDE = {
  khroma: 'MIT',
};

const UNSPECIFIED_LICENSE = 'Not specified';

const LICENSE_MAPPER = {
  'MIT OR SEE LICENSE IN FEEL-FREE.md': 'MIT',
  'SEE LICENSE IN LICENSE': 'MIT',
};

const IGNORED_DEPENDENCIES = [
  'com.nimmsta.workflow.commons.entities:library',
  '@nimmsta:ng-b2b-lib',
  '@nimmsta:ng-commons-lib',
  '@nimmsta:ng-workflow-lib',
];

@Injectable({ providedIn: 'root' })
export class SbomService {
  private http = inject(HttpClient);
  private configuration = inject(NimmstaCommonsModuleConfiguration);

  private getForConfigurator(): Observable<SbomConfiguration> {
    if (!this.configuration.apiUrls.configurations) {
      return of({});
    }
    return this.http
      .get<SbomConfiguration>(
        `${this.configuration.apiUrls.configurations}/actuator/sbom/application`,
      )
      .pipe(catchError(() => of({})));
  }

  private getForDevices(): Observable<SbomConfiguration> {
    if (!this.configuration.apiUrls.b2b) {
      return of({});
    }
    return this.http.get<SbomConfiguration>(
      `${this.configuration.apiUrls.b2b}/actuator/sbom/application`,
    );
  }

  private getForWorkflow(): Observable<SbomConfiguration> {
    if (!this.configuration.apiUrls.workflow) {
      return of({});
    }
    return this.http.get<SbomConfiguration>(
      `${this.configuration.apiUrls.workflow}/actuator/sbom/application`,
    );
  }

  private getForClient(): Observable<SbomConfiguration> {
    return this.http
      .get<SbomConfiguration>('/.well-known/sbom')
      .pipe(catchError(() => of({})));
  }

  public getServerLicenses(): Observable<License[]> {
    return zip(
      this.getForConfigurator(),
      this.getForDevices(),
      this.getForWorkflow(),
    ).pipe(
      map(([configurator, devices, workflow]) => {
        const licenses = new Map<string, License>();
        this.fillLicenses(licenses, configurator);
        this.fillLicenses(licenses, devices);
        this.fillLicenses(licenses, workflow);
        return this.sortLicenses(Array.from(licenses.values()));
      }),
    );
  }

  public getClientLicenses(): Observable<License[]> {
    return this.getForClient().pipe(
      map((clientSbom) => {
        const licenses = new Map<string, License>();
        this.fillLicenses(licenses, clientSbom);
        return this.sortLicenses(Array.from(licenses.values()));
      }),
    );
  }

  private sortLicenses(licenses: License[]): License[] {
    return licenses
      .sort((a, b) => {
        if (a.name === UNSPECIFIED_LICENSE) {
          return 1;
        }
        if (b.name === UNSPECIFIED_LICENSE) {
          return -1;
        }
        return a.name.localeCompare(b.name);
      })
      .map((license) => ({
        ...license,
        dependencies: license.dependencies.sort((a, b) => {
          const aString = a.group
            ? `${a.group}:${a.name}:${a.version}`
            : `${a.name}:${a.version}`;
          const bString = b.group
            ? `${b.group}:${b.name}:${b.version}`
            : `${b.name}:${b.version}`;
          return aString.localeCompare(bString);
        }),
      }));
  }

  private fillLicenses(
    licenses: Map<string, License>,
    sbom: SbomConfiguration,
  ) {
    if (Object.keys(sbom).length === 0) {
      // Error in the request, already handled
      return;
    }
    const firstLevelDependencies = this.findFirstLevelDependencies(sbom);
    if (firstLevelDependencies.length === 0) {
      console.warn('No first level dependencies found');
      return;
    }
    sbom.components
      ?.filter((component) =>
        firstLevelDependencies.includes(component['bom-ref']),
      )
      .forEach((component) => {
        if (
          IGNORED_DEPENDENCIES.includes(`${component.group}:${component.name}`)
        ) {
          return;
        }
        let license = this.findLicense(
          this.resolveExpressions(component.licenses),
        )?.license ?? { id: UNSPECIFIED_LICENSE };
        const override =
          OVERRIDE[
            `${component.group}:${component.name}` as keyof typeof OVERRIDE
          ] ?? OVERRIDE[component.name as keyof typeof OVERRIDE];
        if (override) {
          license = { id: override };
        }
        const id = license.id ?? license.name ?? UNSPECIFIED_LICENSE;
        const savedLicense = licenses.get(id) ?? { name: id, dependencies: [] };
        if (
          savedLicense.dependencies.find(
            (dep) =>
              dep.group === component.group &&
              dep.name === component.name &&
              dep.version === component.version,
          )
        ) {
          return;
        }
        savedLicense.dependencies.push({
          group: component.group,
          name: component.name,
          version: component.version,
          licenseUrl: license.url,
        });
        licenses.set(id, savedLicense);
      });
  }

  private findFirstLevelDependencies(
    dependencies: SbomConfiguration,
  ): string[] {
    const bomRef = dependencies.metadata?.component?.['bom-ref'];
    if (!bomRef) {
      console.warn('No bom-ref found');
      return [];
    }
    // Include the direct dependencies and indirect dependencies from nimmsta related libraries
    return (
      dependencies.dependencies
        ?.filter(
          (dependency) =>
            dependency.ref === bomRef ||
            dependency.ref.toLowerCase().includes('nimmsta'),
        )
        .flatMap((dependency) => dependency.dependsOn) ?? []
    );
  }

  private resolveExpressions(licenses?: SbomLicense[]): SbomLicense[] {
    return (
      licenses
        ?.map((license) => {
          if (!license.expression) {
            return [license];
          }
          const resolvedLicenses = this.resolveExpression(license.expression);
          if (!license.license) {
            return resolvedLicenses ?? [];
          }
          const originalLicense = { license: license.license };
          return resolvedLicenses
            ? [...resolvedLicenses, originalLicense]
            : [originalLicense];
        })
        .flatMap((license) => license) ?? []
    );
  }

  private resolveExpression(expression: string): SbomLicense[] | undefined {
    if (!expression.match(/^\(.*?(OR .*?)*\)$/)) {
      console.warn('Invalid expression');
      return undefined;
    }
    const cleanExpression = expression.slice(1, -1);
    const parts = cleanExpression.split('OR ');
    return parts.map((part) => ({ license: { name: part.trim() } }));
  }

  private findLicense(licenses: SbomLicense[]): SbomLicense | undefined {
    const mappedLicenses = licenses.map((license) => ({
      license: {
        id:
          LICENSE_MAPPER[license.license?.id as keyof typeof LICENSE_MAPPER] ??
          license.license?.id,
        name:
          LICENSE_MAPPER[
            license.license?.name as keyof typeof LICENSE_MAPPER
          ] ?? license.license?.name,
        url: license.license?.url,
      },
    }));
    if (mappedLicenses.length === 0) {
      return undefined;
    }
    if (mappedLicenses.length === 1) {
      return mappedLicenses[0];
    }

    const filteredLicenses = mappedLicenses.filter((license) =>
      LICENSE_PRIORITY.includes(license.license.id ?? license.license.name),
    );
    if (filteredLicenses.length === 1) {
      return filteredLicenses[0];
    }
    if (filteredLicenses.length === 0) {
      console.warn(
        'No viable license found for component, please adapt configuration',
      );
      return undefined;
    }
    const sortedLicenses = filteredLicenses.sort(
      (a, b) =>
        LICENSE_PRIORITY.indexOf(a.license.id ?? a.license.name) -
        LICENSE_PRIORITY.indexOf(b.license.id ?? b.license.name),
    );
    return sortedLicenses[0];
  }
}
